/**
 * WalletAuthService.js — Couche d'accès API pour EarniWalletModal.
 *
 * Couvre les 9 endpoints EarniConnect réels (SPEC §4 / auth.route.js) :
 *   - SIWE  : GET /auth/nonce, POST /auth/verify-signature
 *   - Email : POST /auth/email-signup, POST /auth/email-signin
 *   - Wallet: GET /auth/wallets, POST /auth/wallet/link, DELETE /auth/wallet/:id,
 *             POST /auth/wallet/switch, POST /auth/wallet/key (step1 + step2)
 *
 * Utilise `axiosWithoutEncryption` — utilitaire existant pour les routes auth non chiffrées.
 * La route `GET /auth/wallets` et toutes les routes /auth/wallet/* nécessitent un JWT Bearer ;
 * `axiosWithoutEncryption` l'attache automatiquement depuis localStorage._user quand l'URL
 * contient 'user/' (fallback) — MAIS pour /wallets et /wallet/*, le token n'est PAS attaché
 * par la whitelist actuelle de axiosWithoutEncryption (qui exclut /auth sauf 'user/' et 'signout').
 *
 * IMPORTANT — comportement actuel de la whitelist (apiCallWithToken.js ligne 199-204) :
 *   attachAuth = hasToken && (!url.includes('/auth') || url.includes('signout') || url.includes('user/'))
 *   → /auth/wallets et /auth/wallet/* ne passent PAS la whitelist → Authorization ABSENT.
 *
 * Pour les appels JWT requis (getLinkedWallets, linkWallet, deleteWallet, switchWallet,
 * requestKeyExportOtp, exportKey), on passe le token manuellement via le 4e paramètre `params`
 * non, via un extraHeaders simulé → on lit directement localStorage._user ici pour construire
 * le header Authorization et l'injecter via axiosWithToken à la place.
 *
 * CHOIX TECHNIQUE : on utilise axiosWithToken (avec chiffrement) pour les appels authentifiés
 * afin de rester cohérent avec les autres services du projet. Pour les appels publics (nonce,
 * verify-signature, email-signup, email-signin, wallet/register) on utilise axiosWithoutEncryption.
 *
 * Flux REGISTER (wallet externe inconnu) : verify-signature renvoie { action:'REGISTER',
 * registrationToken } → WalletRegisterPanel appelle registerWithWallet() → POST /auth/wallet/register
 * consomme le registrationToken et crée le compte + lie le wallet en une transaction.
 */

import { axiosWithoutEncryption } from '../../../utils/functions/apiCallWithToken';
import { axiosWithToken }         from '../../../utils/functions/apiCallWithToken';
import { fetchSiweNonce }         from '../../../utils/functions/siweNonce';

/* ─── SIWE ───────────────────────────────────────────────────────────────────── */

/**
 * GET /auth/nonce?address=<address>
 * Retourne { nonce: <message EIP-4361 COMPLET>, message, expiresAt }
 * Le champ `nonce` et le champ `message` sont identiques et contiennent le message
 * complet à signer (voir contrôleur getSiweNonce : `nonce: message, message, expiresAt`).
 *
 * @param {string} base    - URL de base (ex: process.env.REACT_APP_SERVER)
 * @param {string} address - adresse 0x du wallet
 * @returns {Promise<{ nonce: string, message: string, expiresAt: string }>}
 */
export const getNonce = (base, address) =>
  // Dédupliqué (cf. siweNonce.js) : partage le même nonce avec le handler global accountsChanged
  // et les autres instances <EarniWalletModal> → un seul upsert serveur → pas de race nonce.
  fetchSiweNonce(base, address);

/**
 * POST /auth/verify-signature
 * Body: { message, signature, address }
 * Retourne soit un objet user + accessToken (wallet existant),
 * soit { action: 'REGISTER', address, verifiedAt } (nouvelle adresse).
 *
 * @param {string} base
 * @param {{ message: string, signature: string, address: string }} payload
 * @returns {Promise<object>}
 */
export const verifySignature = (base, payload) =>
  axiosWithoutEncryption(`${base}/auth/verify-signature`, 'post', payload);

/* ─── Email auth ─────────────────────────────────────────────────────────────── */

/**
 * POST /auth/email-signin
 * Body: { email, password }
 * Retourne l'objet user + accessToken, ou lève une erreur (401/403).
 *
 * @param {string} base
 * @param {{ email: string, password: string }} payload
 * @returns {Promise<object>}
 */
export const emailSignIn = (base, payload) =>
  axiosWithoutEncryption(`${base}/auth/email-signin`, 'post', payload);

/**
 * POST /auth/email-signup
 * Body: { email, password, username }
 * Retourne { id, email, username, metamask, isVerified, accessToken }.
 *
 * @param {string} base
 * @param {{ email: string, password: string, username: string }} payload
 * @returns {Promise<object>}
 */
export const emailSignUp = (base, payload) =>
  axiosWithoutEncryption(`${base}/auth/email-signup`, 'post', payload);

/**
 * registerWithWallet — crée un compte depuis un wallet externe vérifié SIWE.
 * POST /auth/wallet/register (non authentifié : consomme le registrationToken émis par
 * verify-signature). payload : { registrationToken, username, email?, walletType? }.
 * Réponse 201 : user complet + accessToken.
 *
 * @param {string} base
 * @param {object} payload  { registrationToken, username, email?, walletType? }
 */
export const registerWithWallet = (base, payload) =>
  axiosWithoutEncryption(`${base}/auth/wallet/register`, 'post', payload);

/* ─── Wallet management (JWT requis) ─────────────────────────────────────────── */

/**
 * GET /auth/wallets
 * Retourne { wallets: Array<{ id, address, walletType, isPrimary, createdAt }> }.
 * Nécessite un JWT Bearer. Utilise axiosWithToken (chiffrement + auth).
 *
 * @param {string} base
 * @returns {Promise<{ wallets: Array<object> }>}
 */
export const getLinkedWallets = (base) =>
  axiosWithToken(`${base}/auth/wallets`, 'get', {});

/**
 * POST /auth/wallet/link
 * Body: { message, signature, address, label?, walletType? }
 * Retourne { wallet: { id, address, walletType, isPrimary, createdAt } }.
 * Nécessite un JWT Bearer.
 *
 * @param {string} base
 * @param {{ message: string, signature: string, address: string, label?: string, walletType?: string }} payload
 * @returns {Promise<{ wallet: object }>}
 */
export const linkWallet = (base, payload) =>
  axiosWithToken(`${base}/auth/wallet/link`, 'post', payload);

/**
 * DELETE /auth/wallet/:walletId
 * Retourne { message: 'wallet unlinked successfully' }.
 * Nécessite un JWT Bearer.
 *
 * @param {string} base
 * @param {number|string} walletId
 * @returns {Promise<{ message: string }>}
 */
export const deleteWallet = (base, walletId) =>
  axiosWithToken(`${base}/auth/wallet/${walletId}`, 'delete', {});

/**
 * POST /auth/wallet/switch
 * Body: { walletId }
 * Retourne l'objet user + nouveau accessToken (JWT mis à jour avec le nouveau metamask).
 * Nécessite un JWT Bearer.
 *
 * @param {string} base
 * @param {{ walletId: number }} payload
 * @returns {Promise<object>}
 */
export const switchWallet = (base, payload) =>
  axiosWithToken(`${base}/auth/wallet/switch`, 'post', payload);

/**
 * POST /auth/wallet/disown — SPEC-2026-06-24-wallet-kek-migration.v1 PR #4
 * Body: { walletId, confirmed: true }
 * Révoque la copie chiffrée du password originel détenue côté serveur (RGPD Art. 17).
 * Après disown : reset password = wallet perdu définitivement.
 * Idempotent : retourne { alreadyDisabled: true } si recovery déjà NULL.
 * Rate-limit backend : 3 requêtes/heure. Nécessite un JWT Bearer.
 *
 * @param {string} base
 * @param {{ walletId: number, confirmed: true }} payload
 * @returns {Promise<{ message: string, alreadyDisabled: boolean }>}
 */
export const disownWallet = (base, payload) =>
  axiosWithToken(`${base}/auth/wallet/disown`, 'post', payload);

/* ─── Export de clé privée — two-step (JWT requis) ──────────────────────────── */

/**
 * POST /auth/wallet/key — Step 1 : demande d'OTP
 * Body: { walletId, requestOtp: true }
 * Retourne { message: 'OTP sent to your registered email address' }.
 * Nécessite un JWT Bearer.
 * Rate-limit backend : 3 requêtes/heure toutes étapes confondues.
 *
 * @param {string} base
 * @param {number|string} walletId
 * @returns {Promise<{ message: string }>}
 */
export const requestKeyExportOtp = (base, walletId) =>
  axiosWithToken(`${base}/auth/wallet/key`, 'post', { walletId, requestOtp: true });

/**
 * POST /auth/wallet/key — Step 2 : vérification OTP + export keystore
 * Body: { walletId, password, otpCode }
 * Retourne { keystore: string, mnemonic: string, address: string }.
 * Nécessite un JWT Bearer.
 * La clé privée en clair ne transite JAMAIS dans la réponse (SPEC §9).
 *
 * @param {string} base
 * @param {{ walletId: number, password: string, otpCode: string }} payload
 * @returns {Promise<{ keystore: string, mnemonic: string, address: string }>}
 */
export const exportKey = (base, payload) =>
  axiosWithToken(`${base}/auth/wallet/key`, 'post', payload);
